import { DatePicker, Form } from 'antd';
import { FormField, FormSelect, FormTitle } from '../form/FormBasic';
import { FormFieldTypeEnum } from '../../enums/FormFieldTypeEnum';
import { FormInstance, useWatch } from 'antd/es/form/Form';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import PrimaryButton from '../buttons/PrimaryButton';
import SecondaryButton from '../buttons/SecondaryButton';
import { useCollectionSessionForm } from '../../hooks/price/useCollectionSessionForm';
import { REGEX_ALL_ALLOWED_SAFE_FILTER } from '../../constants/regex';
import { Configuration } from '../../constants/configs';
import {
  maxLengthRule,
  minLengthRule,
  noSpacesOnlyRule,
  regexRule,
  requiredRule,
} from '../../helpers/formHelper';

import { PriceRecordFormProps } from '../../types/priceTypes';
import OfflineSyncWarning from '../common/OfflineSyncWarning';
import DefaultButton from '../buttons/DefaultButton';

const CollectionSessionInfoForm: React.FC<PriceRecordFormProps> = ({
  form,
  handleNext,
  isCitizen,
  finishRegistrationClicked,
  triggeredFromTask,
  taskData,
}) => {
  const { t } = useTranslation('price');

  const {
    departmentsOptions,
    municipalityOptions,
    typeOfPositionOptions,
    establishmentOptions,
    showEstablishmentTextField,
    continueButtonClicked,
    setContinueButtonClicked,
    loadingLocations,
    loadingEstablishments,
    isLocationsOfflineAndEmpty,
  } = useCollectionSessionForm({
    form,
    triggeredFromTask,
    taskData,
  });

  const dateVal = useWatch('date', form);
  const departmentVal = useWatch('department', form);
  const municipalityVal = useWatch('municipality', form);
  const typeOfPositionVal = useWatch('typeOfPosition', form);
  const establishmentVal = useWatch('establishment', form);
  const establishmentOtherVal = useWatch('establishmentOther', form);

  useEffect(() => {
    if (finishRegistrationClicked === 0) {
      setContinueButtonClicked(false);
    }
  }, [finishRegistrationClicked, setContinueButtonClicked]);

  const handleContinueAddProduct = () => {
    if (!isCitizen) {
      setContinueButtonClicked(true);
    }
    handleNext();
  };

  const enableLast15Days = (current: any) => {
    const today = dayjs();
    const last15Days = today.subtract(14, 'day');
    return current.isBefore(last15Days, 'day') || current.isAfter(today, 'day');
  };

  const hasAllFieldsValue =
    !!dateVal &&
    !!departmentVal &&
    !!municipalityVal &&
    !!typeOfPositionVal &&
    !!establishmentVal &&
    (!showEstablishmentTextField || !!establishmentOtherVal?.trim());

  useEffect(() => {
    if (isCitizen && hasAllFieldsValue) {
      handleContinueAddProduct();
    }
  }, [hasAllFieldsValue, isCitizen]);

  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    if (continueButtonClicked) {
      setIsCollapsed(true);
    } else {
      setIsCollapsed(false);
    }
  }, [continueButtonClicked]);

  return (
    <>
      {!isCitizen && (
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <FormTitle
            label={t('collectionSessionInformation')}
            styles="text-lg md:text-xl font-semibold text-black mb-0"
          />
          {continueButtonClicked && isCollapsed && (
            <DefaultButton
              type="button"
              handleOnClick={() => setIsCollapsed(false)}
              className="self-start rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-semibold text-primary transition-colors hover:bg-primary/10 focus:outline-none sm:self-auto"
            >
              {t('showSessionInfo', 'Show Session Info')}
            </DefaultButton>
          )}
          {continueButtonClicked && !isCollapsed && (
            <DefaultButton
              type="button"
              handleOnClick={() => setIsCollapsed(true)}
              className="self-start rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-100 focus:outline-none sm:self-auto"
            >
              {t('hideSessionInfo', 'Hide Session Info')}
            </DefaultButton>
          )}
        </div>
      )}

      {isLocationsOfflineAndEmpty && <OfflineSyncWarning />}

      {continueButtonClicked && isCollapsed ? (
        <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div className="text-sm text-gray-700">
            <span className="font-semibold">
              {establishmentOptions?.find((opt: any) => opt.value === establishmentVal)?.label ||
                establishmentOtherVal ||
                t('collectionSessionInformation')}
            </span>
          </div>
        </div>
      ) : (
        <Form
          form={form}
          name="collectionSessionInfoDataForm"
          layout="vertical"
          onFinish={handleNext}
          initialValues={{
            date: dayjs(),
            department: Configuration.defaultLocation.department,
            municipality: Configuration.defaultLocation.municipality,
          }}
        >
          {isCitizen ? (
            <Form.Item
              name="date"
              label={t('dateLabel')}
              rules={[{ required: true, message: t('dateFieldRequired') }]}
            >
              <DatePicker
                className="w-full"
                showTime={false}
                showSecond={false}
                disabledDate={enableLast15Days}
                format="YYYY-MM-DD"
                inputReadOnly={true}
              />
            </Form.Item>
          ) : (
            <Form.Item
              name="date"
              label={t('dateLabel')}
              rules={[{ required: true, message: t('dateFieldRequired') }]}
            >
              <DatePicker
                className="w-full"
                showTime={false}
                showSecond={false}
                disabled={true}
                format="YYYY-MM-DD"
              />
            </Form.Item>
          )}

          <FormSelect
            name="department"
            label={t('departmentLabel')}
            rules={[{ required: true, message: t('departmentFieldRequired') }]}
            placeholder={t('departmentFieldPlaceholder')}
            disabled={continueButtonClicked || isLocationsOfflineAndEmpty}
            isRequired={false}
            allowClear={true}
            options={departmentsOptions}
            loading={loadingLocations}
          />

          <FormSelect
            name="municipality"
            label={t('municipalityLabel')}
            rules={[{ required: true, message: t('municipalityFieldRequired') }]}
            placeholder={t('municipalityFieldPlaceholder')}
            disabled={continueButtonClicked || isLocationsOfflineAndEmpty}
            isRequired={false}
            allowClear={true}
            options={municipalityOptions}
            loading={loadingLocations}
          />

          <FormSelect
            name="typeOfPosition"
            label={t('typeOfPositionLabel')}
            rules={[{ required: true, message: t('typeOfPositionFieldRequired') }]}
            placeholder={t('typeOfPositionFieldPlaceholder')}
            disabled={continueButtonClicked}
            isRequired={false}
            allowClear={true}
            options={typeOfPositionOptions}
            loading={loadingEstablishments}
          />

          <FormSelect
            name="establishment"
            options={establishmentOptions}
            label={t('establishmentLabel')}
            rules={[{ required: true, message: t('establishmentFieldRequired') }]}
            placeholder={t('establishmentFieldPlaceholder')}
            disabled={continueButtonClicked}
            isRequired={false}
            allowClear={true}
            loading={loadingEstablishments}
          />

          {showEstablishmentTextField && (
            <FormField
              name="establishmentOther"
              label=""
              placeholder={t('establishmentOtherFieldPlaceholder')}
              isRequired={false}
              type={FormFieldTypeEnum.INPUT}
              allowLeadingTrailingSpaces={true}
              useCustomRules={true}
              rules={[
                requiredRule(t, t('establishmentOtherFieldPlaceholder')),
                noSpacesOnlyRule(t, t('establishmentOtherFieldPlaceholder')),
                minLengthRule(t, t('establishmentOtherFieldPlaceholder'), 1),
                maxLengthRule(t, t('establishmentOtherFieldPlaceholder'), 100),
                regexRule(t, REGEX_ALL_ALLOWED_SAFE_FILTER),
              ]}
            />
          )}

          {!isCitizen && !continueButtonClicked && (
            <div className="mt-6 flex items-center justify-between gap-6">
              <PrimaryButton
                handleOnClick={handleContinueAddProduct}
                disabled={!hasAllFieldsValue}
                width="100%"
                height="40px"
                hideBgGradient={true}
                hidden={!hasAllFieldsValue}
                className="flex items-center justify-center"
              >
                {t('continueAddingPresentation')}
              </PrimaryButton>
            </div>
          )}
        </Form>
      )}
    </>
  );
};

export default CollectionSessionInfoForm;
